import {
  ArrowDownOnSquareIcon,
  ArrowUpTrayIcon,
  BookmarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Save } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { DocumentListItem } from '@/components/documents/DocumentListItem';
import { DocumentSource } from '@/components/documents/documentSources';
import {
  sortFiles,
  SortKey,
  SortState,
} from '@/components/documents/sortFiles';
import FileViewer from '@/components/FileViewer';
import { NoData } from '@/components/legacy-ui/NoData';
import {
  createAllEmptyConfig,
  createFilteredEmptyConfig,
  NO_DATA_ICON_CLASS,
} from '@/components/legacy-ui/utils/createNoDataConfig';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { useDeleteFile, useDownloadFile, useMy, useUploadFile } from '@/hooks';
import { UploadFileWizard } from '@/sections/files/components/UploadFileWizard';
import { MyFile, ParsedFileTypes } from '@/types';
import { cn } from '@/utils/classname';
import { encode } from '@/utils/encrypt.util';
import { getFileType } from '@/utils/file.util';
import { useStorage } from '@/utils/storage/useStorage.util';

interface SortableHeaderProps {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onChange: (next: SortState) => void;
  className?: string;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  sortKey,
  sort,
  onChange,
  className,
}) => {
  const isActive = sort.key === sortKey;
  const next: SortState = isActive
    ? { key: sortKey, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
    : {
        key: sortKey,
        direction: sortKey === 'date' ? 'desc' : 'asc',
      };
  // Native button semantics are kept (no role override): columnheader/aria-sort
  // are only valid inside table/grid rows, so sort state goes in the name.
  return (
    <button
      type="button"
      onClick={() => onChange(next)}
      className={cn(
        'group flex items-center gap-1 text-left text-xs font-semibold uppercase',
        'text-chariot-text-secondary hover:text-chariot-text-primary',
        className
      )}
      aria-label={`Sort by ${label}${
        isActive
          ? `, ${sort.direction === 'asc' ? 'ascending' : 'descending'}`
          : ''
      }`}
    >
      <span className="truncate">{label}</span>
      {isActive ? (
        sort.direction === 'asc' ? (
          <ChevronUpIcon className="text-chariot-text-primary size-3.5 shrink-0" />
        ) : (
          <ChevronDownIcon className="text-chariot-text-primary size-3.5 shrink-0" />
        )
      ) : (
        <ChevronUpIcon className="size-3.5 shrink-0 opacity-0 group-hover:opacity-60" />
      )}
    </button>
  );
};

const FilesHeaderRow: React.FC<{
  sort: SortState;
  onChange: (next: SortState) => void;
}> = ({ sort, onChange }) => (
  <div className="border-chariot-stroke flex items-center gap-4 border-b px-4 py-2">
    <SortableHeader
      label="Name"
      sortKey="name"
      sort={sort}
      onChange={onChange}
      className="min-w-0 flex-1"
    />
    <SortableHeader
      label="Date"
      sortKey="date"
      sort={sort}
      onChange={onChange}
      className="w-32 shrink-0"
    />
    <SortableHeader
      label="Source"
      sortKey="source"
      sort={sort}
      onChange={onChange}
      className="w-28 shrink-0"
    />
    <SortableHeader
      label="Visibility"
      sortKey="visibility"
      sort={sort}
      onChange={onChange}
      className="w-24 shrink-0"
    />
    <div className="w-8 shrink-0"></div>
    <div className="w-8 shrink-0"></div>
  </div>
);

interface DocumentsSectionsProps {
  query?: string;
  customerEmail?: string;
  enableUpload?: boolean;
  enableSearch?: boolean;
  enableFavorites?: boolean;
  isPraetorianUser?: boolean; // Determines which section can upload
  // Opt-in for the hardcoded `reports/` (PlexTrac reports) listing. Default
  // false so report files don't leak into the generic Files view — Reports
  // live in their own tab and pass `includeReports` explicitly.
  includeReports?: boolean;
  // Opt-in for the Letter of Attestation listing (the home/shared-with listing
  // filtered to the `letter-of-attestation` filename). Default false so LOA
  // files don't leak into the generic Files view — LOAs live in their own tab
  // and pass `includeLOA` explicitly. Symmetric with `includeReports`.
  includeLOA?: boolean;
  // Whether the Praetorian-only "Internal Documents" box may render. Default
  // true preserves the Files tab. Reports/LOA pass false — those tabs hold
  // only system-generated docs, so the internal box must never appear there.
  showInternalSection?: boolean;
  // Optional empty-state override. When the tab is completely empty, these
  // drive the NoData title/description/CTA. `onAction` (when provided) becomes
  // the empty-state button; the component stays agnostic about what it does.
  emptyState?: {
    title: string;
    description?: React.ReactNode;
    actionLabel?: string;
    onAction?: () => void;
  };
  // Overrides the header `<h2>` text (default 'Documents'). Used by the
  // Reports/LOA tabs to title their header without forking the component.
  heading?: string;
  // Optional right-aligned action rendered ONLY in the emptiness-gated header,
  // so it auto-hides when the tab is empty (the centered empty-state button is
  // the sole CTA then). Reports/LOA pass their Generate button here.
  headerAction?: React.ReactNode;
}

export const DocumentsSections: React.FC<DocumentsSectionsProps> = ({
  query = 'home',
  customerEmail,
  enableUpload = true,
  enableSearch = true,
  enableFavorites = true,
  isPraetorianUser = false, // Default to non-Praetorian for security — callers must explicitly opt in
  includeReports = false, // Default off — reports are opt-in (own tab), not part of the generic Files view
  includeLOA = false, // Default off — LOAs are opt-in (own tab), not part of the generic Files view
  showInternalSection = true,
  emptyState,
  heading = 'Documents',
  headerAction,
}) => {
  // Role-based upload permissions
  const canUpload = enableUpload;
  // useUploadFile is retained solely for the file-viewer Save button below; the
  // inline upload paths were replaced by UploadFileWizard.
  const { mutateAsync: uploadFile } = useUploadFile(customerEmail);
  const { mutate: downloadFile } = useDownloadFile(customerEmail);
  const { mutateAsync: deleteFile } = useDeleteFile(customerEmail);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filename, setFilename] = useState(
    () => searchParams.get('file') || ''
  );
  const [content, setContent] = useState<string | null>(null);
  const [originalContent, setOriginalContent] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({
    key: 'date',
    direction: 'desc',
  });
  const [favorites, setFavorites] = useStorage<string[]>(
    { key: 'files/favorites' },
    []
  );

  React.useEffect(() => {
    if (searchParams.get('file')) {
      setSearchParams(
        prev => {
          prev.delete('file');
          return prev;
        },
        { replace: true }
      );
    }
  }, []);

  const {
    data: sharedByCustomerFiles = [],
    invalidate: invalidateSharedByCustomerFiles,
    status: sharedByCustomerFilesStatus,
  } = useMy(
    {
      resource: 'file',
      query: `${query}/shared-by`,
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  const {
    data: sharedWithCustomerFiles = [],
    invalidate: invalidateSharedWithCustomerFiles,
    status: sharedWithCustomerFilesStatus,
  } = useMy(
    {
      resource: 'file',
      query: `${query}/shared-with`,
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  // Query PlexTrac reports stored in reports/ directory.
  // Opt-in only: the query is disabled unless `includeReports` is set, so the
  // generic Files view doesn't surface report files (they have their own tab).
  const {
    data: plextracReports = [],
    invalidate: invalidatePlextracReports,
    status: plextracReportsStatus,
  } = useMy(
    {
      resource: 'file',
      query: 'reports/',
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
      enabled: includeReports,
    }
  );

  // Lists the home/shared-with/ prefix and is filtered (see `loaDocuments`) to
  // the system-generated `letter-of-attestation` filename to populate the LOA
  // tab. LOAs predating the backend path revert (from loa/ back to
  // home/shared-with/) live here too, so both old and new LOAs surface without
  // a data migration.
  // Opt-in only: the query is disabled unless `includeLOA` is set, so the
  // generic Files view doesn't surface LOA files (they have their own tab).
  const {
    data: loaFiles = [],
    invalidate: invalidateLoaFiles,
    status: loaFilesStatus,
    fetchNextPage: fetchNextLoaPage,
    hasNextPage: hasNextLoaPage,
    isFetching: isFetchingLoa,
  } = useMy(
    {
      resource: 'file',
      query: 'home/shared-with/',
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
      enabled: includeLOA,
    }
  );

  // Query export files stored under home/export-*
  const {
    data: exportFiles = [],
    invalidate: invalidateExportFiles,
    status: exportFilesStatus,
  } = useMy(
    {
      resource: 'file',
      query: `${query}/export-`,
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  // Query internal documents (Praetorian-only)
  const {
    data: internalFiles = [],
    invalidate: invalidateInternalFiles,
    status: internalFilesStatus,
  } = useMy(
    {
      resource: 'file',
      query: `${query}/internal`,
      praetorian: true,
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
      enabled: isPraetorianUser && showInternalSection,
    }
  );

  // Drive the LOA infinite query to exhaustion. `useMy` pages ~100 rows at a
  // time; without this, `loaFiles` would only ever hold the first page, so an
  // LOA pushed past row ~100 by other files in the shared `home/shared-with/`
  // prefix would silently disappear from the LOA tab. We fetch the next page
  // whenever one exists and we aren't already fetching — the effect re-runs as
  // each page lands (hasNextLoaPage / isFetchingLoa change) until
  // `hasNextLoaPage` is false, at which point `loaFiles` holds the COMPLETE
  // set. Guarded on `hasNextLoaPage` so there is no infinite loop.
  React.useEffect(() => {
    if (includeLOA && hasNextLoaPage && !isFetchingLoa) {
      fetchNextLoaPage();
    }
  }, [includeLOA, hasNextLoaPage, isFetchingLoa, fetchNextLoaPage]);

  // LOAs are identified by the system-generated "letter-of-attestation" filename
  // (BuildLOAPath), not by a dedicated prefix — so old and new LOAs both qualify.
  const loaDocuments = useMemo(
    () => loaFiles.filter(f => f.name.includes('letter-of-attestation')),
    [loaFiles]
  );
  // Keep LOAs out of the generic shared-with section; they have their own tab.
  const sharedWithNonLoaFiles = useMemo(
    () =>
      sharedWithCustomerFiles.filter(
        f => !f.name.includes('letter-of-attestation')
      ),
    [sharedWithCustomerFiles]
  );

  const files = useMemo(() => {
    return [
      ...sharedByCustomerFiles,
      ...sharedWithNonLoaFiles,
      ...(includeReports ? plextracReports : []),
      ...(includeLOA ? loaDocuments : []),
      ...exportFiles,
      ...(isPraetorianUser && showInternalSection ? internalFiles : []),
    ];
  }, [
    sharedByCustomerFiles,
    sharedWithNonLoaFiles,
    plextracReports,
    loaDocuments,
    exportFiles,
    internalFiles,
    isPraetorianUser,
    includeReports,
    includeLOA,
    showInternalSection,
  ]);

  type TaggedFile = MyFile & {
    source: DocumentSource;
    displayName: string;
  };

  const taggedFiles = useMemo<TaggedFile[]>(() => {
    const tagged = [
      ...(isPraetorianUser && showInternalSection
        ? internalFiles.map(f => ({
            ...f,
            source: 'internal' as const,
            displayName: f.name.replace(`${query}/internal/`, ''),
          }))
        : []),
      ...exportFiles.map(f => ({
        ...f,
        source: 'export' as const,
        displayName: f.name.replace(`${query}/`, ''),
      })),
      ...sharedWithNonLoaFiles.map(f => ({
        ...f,
        source: 'shared-with' as const,
        displayName: f.name.replace(`${query}/shared-with/`, ''),
      })),
      ...(includeReports
        ? plextracReports.map(f => ({
            ...f,
            source: 'report' as const,
            displayName: f.name.replace('reports/', ''),
          }))
        : []),
      ...(includeLOA
        ? loaDocuments.map(f => ({
            ...f,
            source: 'loa' as const,
            displayName: f.name.replace('home/shared-with/', ''),
          }))
        : []),
      ...sharedByCustomerFiles.map(f => ({
        ...f,
        source: 'shared-by' as const,
        displayName: f.name.replace(`${query}/shared-by/`, ''),
      })),
    ];

    // One physical file can satisfy two prefix queries (e.g. a `reports/export-*`
    // object matches both the dedicated `reports/` query and a broad `export-`
    // listing), yielding two entries with the same `file.name` → a duplicate
    // React key + double render. Dedupe by name, keeping the more-specific
    // source (dedicated reports/LOA/internal listings outrank the generic
    // shared/export prefix listings).
    const sourcePriority: Record<DocumentSource, number> = {
      internal: 5,
      report: 4,
      loa: 3,
      'shared-by': 2,
      'shared-with': 1,
      export: 0,
    };
    const byName = new Map<string, TaggedFile>();
    for (const file of tagged) {
      const existing = byName.get(file.name);
      if (
        !existing ||
        sourcePriority[file.source] > sourcePriority[existing.source]
      ) {
        byName.set(file.name, file);
      }
    }
    return [...byName.values()];
  }, [
    sharedWithNonLoaFiles,
    sharedByCustomerFiles,
    plextracReports,
    loaDocuments,
    exportFiles,
    internalFiles,
    isPraetorianUser,
    includeReports,
    includeLOA,
    showInternalSection,
    query,
  ]);

  // Sort once; both lists below are order-preserving filters of this array.
  const sortedTaggedFiles = useMemo(
    () => sortFiles(taggedFiles, sort),
    [taggedFiles, sort]
  );

  const filteredFiles = useMemo(
    () =>
      sortedTaggedFiles.filter(file =>
        // Filter on the user-visible name (matches the table render + sortFiles
        // ordering), not the full storage path the user never sees.
        (file.displayName ?? file.name)
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [sortedTaggedFiles, search]
  );

  const isLoading =
    sharedWithCustomerFilesStatus === 'pending' ||
    sharedByCustomerFilesStatus === 'pending' ||
    (includeReports && plextracReportsStatus === 'pending') ||
    (includeLOA && (loaFilesStatus === 'pending' || hasNextLoaPage)) ||
    exportFilesStatus === 'pending' ||
    (isPraetorianUser &&
      showInternalSection &&
      internalFilesStatus === 'pending');

  const isAllEmpty = !isLoading && taggedFiles.length === 0;

  // Chrome (search bar, header, internal box) must only render once queries
  // have settled. During the loading window isAllEmpty is forced false, so
  // gating on !isAllEmpty alone lets chrome flash in then vanish.
  const showChrome = !isLoading && !isAllEmpty;

  const hasSearchFilter = search.trim().length > 0;
  const noDataConfig = isAllEmpty
    ? createAllEmptyConfig({
        entityName: emptyState?.title ?? 'No Documents Yet',
        icon: <DocumentTextIcon className={`${NO_DATA_ICON_CLASS} size-12`} />,
        description:
          emptyState?.description ?? 'Upload your first file to get started',
        onGetStarted:
          emptyState?.onAction ??
          (canUpload ? () => setUploadOpen(true) : undefined),
        actionLabel: emptyState?.actionLabel ?? 'Upload File',
      })
    : hasSearchFilter
      ? createFilteredEmptyConfig({
          entityName: 'documents',
          icon: (
            <DocumentTextIcon className={`${NO_DATA_ICON_CLASS} size-12`} />
          ),
          hasActiveFilters: true,
          onClearFilters: () => setSearch(''),
        })
      : createAllEmptyConfig({
          entityName: 'No shared documents yet',
          icon: (
            <DocumentTextIcon className={`${NO_DATA_ICON_CLASS} size-12`} />
          ),
        });

  const handleDownload = (file: MyFile) => {
    const isInternal = internalFiles.some(f => f.name === file.name);
    downloadFile({ name: file.name, ...(isInternal && { praetorian: true }) });
  };

  const handleToggleFavorite = (file: MyFile) => {
    if (favorites.includes(file.name)) {
      setFavorites(favorites.filter(fav => fav !== file.name));
    } else {
      setFavorites([...favorites, file.name]);
    }
  };

  const handleDelete = async (file: MyFile) => {
    const isInternal = internalFiles.some(f => f.name === file.name);
    await deleteFile({
      name: file.name,
      ...(isInternal && { praetorian: true }),
    });
    // Remove from favorites if it was favorited
    if (favorites.includes(file.name)) {
      setFavorites(favorites.filter(fav => fav !== file.name));
    }
    // Invalidate all file queries
    invalidateSharedWithCustomerFiles();
    invalidateSharedByCustomerFiles();
    invalidatePlextracReports();
    invalidateLoaFiles();
    invalidateExportFiles();
    invalidateInternalFiles();
  };

  const handleShare = (file: MyFile) => {
    const basePath = customerEmail
      ? `/${encodeURIComponent(encode(customerEmail))}`
      : `/${window.location.pathname.split('/').filter(Boolean)[0] ?? ''}`;
    const params = new URLSearchParams();
    params.append('file', file.name);
    const url = `${window.location.origin}${basePath}/files?${params.toString()}`;
    void navigator.clipboard.writeText(url);
    toast.success('Copied URL to clipboard');
  };

  const fileType = filename ? getFileType(filename) : undefined;
  const isViewingInternalFile = internalFiles.some(f => f.name === filename);
  const hasUnsavedChanges =
    content !== null && originalContent !== null && content !== originalContent;

  return (
    <div className="flex h-full flex-col gap-6 overflow-auto pr-4">
      {enableSearch && showChrome && (
        <>
          <div className="flex items-center justify-between">
            <div className="relative max-w-md grow">
              <input
                placeholder="Search documents..."
                value={search}
                name="file_search"
                onChange={e => setSearch(e.target.value)}
                className="border-chariot-stroke bg-chariot-background-primary h-10 w-full rounded-md border px-4 pl-10"
              />
              <MagnifyingGlassIcon className="text-chariot-text-secondary absolute top-2.5 left-3 size-5" />
              {search.length > 0 && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute top-2.5 right-3"
                >
                  <XMarkIcon className="text-chariot-text-secondary size-5" />
                </button>
              )}
            </div>
          </div>

          <div className="bg-chariot-stroke h-px w-full"></div>
        </>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-6">
        {showChrome && (
          <div className="flex items-start justify-between">
            <h2 className="text-lg font-bold">{heading}</h2>
            {headerAction ??
              (canUpload ? (
                <Button size="sm" onClick={() => setUploadOpen(true)}>
                  <ArrowUpTrayIcon className="size-4" />
                  Upload
                </Button>
              ) : null)}
          </div>
        )}

        <div
          className={cn(
            'border-chariot-stroke flex flex-1 flex-col rounded-md border',
            filteredFiles.length === 0 && !isLoading && 'p-4'
          )}
        >
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center py-12">
              <Loader isLoading type="spinner" />
            </div>
          ) : (
            <>
              {!isAllEmpty && <FilesHeaderRow sort={sort} onChange={setSort} />}
              <div className="flex flex-1 flex-col">
                {filteredFiles.length > 0 ? (
                  filteredFiles.map(file => (
                    <DocumentListItem
                      key={file.name}
                      file={file}
                      onView={() => setFilename(file.name)}
                      onDownload={handleDownload}
                      onDelete={handleDelete}
                      onShare={handleShare}
                      isFavorite={
                        enableFavorites && favorites.includes(file.name)
                      }
                      onToggleFavorite={
                        enableFavorites ? handleToggleFavorite : undefined
                      }
                      displayName={file.displayName}
                      source={file.source}
                    />
                  ))
                ) : (
                  <NoData {...noDataConfig} />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <UploadFileWizard
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        query={query}
        customerEmail={customerEmail}
      />

      <Modal
        open={filename.length > 0}
        onClose={() => {
          setFilename('');
          setContent(null);
          setOriginalContent(null);
        }}
        size="6xl"
        title={
          <div className="flex w-full items-center justify-between">
            <span>{filename}</span>
            <div className="mr-8 flex items-center space-x-2">
              {fileType === ParsedFileTypes.DOCUMENT && (
                <Tooltip
                  title={
                    hasUnsavedChanges
                      ? 'Save Unsaved Changes'
                      : 'Save and Overwrite Existing'
                  }
                >
                  <div className="relative">
                    <Button
                      onClick={async () => {
                        uploadFile({
                          ignoreSnackbar: true,
                          name: filename,
                          content: content ?? '',
                          ...(isViewingInternalFile && { praetorian: true }),
                        }).then(() => {
                          setOriginalContent(content);
                        });
                      }}
                      variant="ghost"
                      className="hover:bg-chariot-background-secondary rounded-xs p-2"
                    >
                      <Save className="size-5" />
                    </Button>
                    {hasUnsavedChanges && (
                      <div className="border-chariot-background-primary absolute -top-1 -right-1 size-3 rounded-full border-2 bg-red-500"></div>
                    )}
                  </div>
                </Tooltip>
              )}
              <Tooltip title="Download">
                <Button
                  onClick={() => {
                    const file = files.find(f => f.name === filename);
                    if (file) handleDownload(file);
                  }}
                  variant="ghost"
                  className="hover:bg-chariot-background-secondary rounded-xs p-2"
                >
                  <ArrowDownOnSquareIcon className="size-5" />
                </Button>
              </Tooltip>
              {enableFavorites && (
                <Tooltip
                  title={
                    favorites.includes(filename)
                      ? 'Remove from favorites'
                      : 'Add to favorites'
                  }
                >
                  <Button
                    onClick={() => {
                      if (favorites.includes(filename)) {
                        setFavorites(favorites.filter(fav => fav !== filename));
                      } else {
                        setFavorites([...favorites, filename]);
                      }
                    }}
                    variant="ghost"
                    className="hover:bg-chariot-background-secondary rounded-xs p-2"
                  >
                    <BookmarkIcon
                      className={`size-5 ${favorites.includes(filename) ? 'fill-current text-yellow-500' : ''}`}
                    />
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        }
        hideFooter
      >
        <FileViewer
          key={filename}
          fileName={filename}
          email={customerEmail}
          praetorian={isViewingInternalFile}
          onChange={changed => {
            setContent(changed);
            if (originalContent === null) {
              setOriginalContent(changed);
            }
          }}
          onClose={() => {
            setFilename('');
            setContent(null);
            setOriginalContent(null);
          }}
        />
      </Modal>
    </div>
  );
};
